import { useRef } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "../seo";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, Instagram, Star } from "lucide-react";
import { CtaBand } from "../components/BookCta";
import { TikTokIcon } from "../components/Footer";
import { MaskedLine, Marquee, ParallaxImage, Reveal } from "../components/motion";
import { IMAGES, OCCASIONS, SERVICE_AREA, INSTAGRAM_URL, INSTAGRAM_HANDLE, GOOGLE_BUSINESS_URL, HONEYBOOK_URL, MEDIA, TIKTOK_URL, TIKTOK_HANDLE } from "../data/site";

const MARQUEE_ITEMS = [
    "Luxury Proposal Setups",
    "McKinney",
    "Outdoor Date Nights",
    "Dallas",
    "Bridal Shower Styling",
    "Frisco & Plano",
    "Birthday Picnics",
    "Anniversaries",
    "Dallas-Fort Worth",
];

const CHAPTERS = [
    {
        n: "01",
        title: "Close to the ground",
        text: "The most meaningful celebrations don't happen at banquet tables. They happen low — on plush floor cushions, around handcrafted tables, where conversation leans in and time slows down.",
    },
    {
        n: "02",
        title: "Candlelight over chandeliers",
        text: "We trade ballrooms for open Texas skies. Dozens of warm LED candles, soft blush linens and minimal refined florals — light that flatters every face and every photograph.",
    },
    {
        n: "03",
        title: "Effortless, always",
        text: "We scout, style, set and strike. You arrive to a finished scene and leave without lifting a cushion. Luxury, for us, means you never think about logistics.",
    },
];

export default function Home() {
    const heroRef = useRef(null);
    const reelRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const heroTextY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);
    const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
    const { scrollYProgress: reelProgress } = useScroll({ target: reelRef, offset: ["start end", "end start"] });
    const reelScale = useTransform(reelProgress, [0, 0.5, 1], [0.92, 1, 1.04]);

    usePageMeta(
        "Enchanting Escapes | Luxury Proposal Setups & Event Styling in McKinney, TX",
        "Enchanting Escapes creates luxury proposal setups, outdoor date nights, bridal shower styling, birthday picnics and anniversary celebrations in McKinney, Dallas, Frisco, Plano and the Dallas-Fort Worth area."
    );

    return (
        <main data-testid="home-page">
            {/* Kinetic Hero — real film */}
            <section ref={heroRef} className="relative h-[100svh] min-h-[620px] overflow-hidden flex items-end" data-testid="home-hero">
                <motion.div
                    className="absolute inset-0 will-change-transform"
                    style={{ y: heroImgY }}
                    initial={{ scale: 1.18 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
                >
                    <video
                        data-testid="hero-video"
                        className="w-full h-full object-cover"
                        src={MEDIA.heroVideo}
                        poster="/media/hero-poster.jpg"
                        autoPlay
                        muted
                        loop
                        playsInline
                    />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D230A]/95 via-[#2D230A]/60 to-[#2D230A]/30" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_25%_80%,rgba(45,35,10,0.55),transparent_60%)]" />

                <motion.img
                    src="/media/logo-navbar.png"
                    alt="Enchanting Escapes logo"
                    data-testid="hero-brand-logo"
                    initial={{ opacity: 0, y: -14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute top-24 sm:top-28 right-5 sm:right-10 w-32 sm:w-44 lg:w-56 pointer-events-none drop-shadow-[0_2px_14px_rgba(45,35,10,0.55)]"
                />

                <motion.div
                    style={{ y: heroTextY, opacity: heroFade }}
                    className="relative max-w-6xl mx-auto px-5 sm:px-8 pb-24 sm:pb-32 w-full"
                >
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.9 }}
                        className="font-accent text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#FFD2CE] mb-6"
                    >
                        Luxury Event Styling · McKinney & Dallas-Fort Worth
                    </motion.p>
                    <h1 className="font-display font-medium tracking-tight leading-[1.02] text-[#FAF7F2] text-5xl sm:text-6xl lg:text-7xl max-w-4xl [text-shadow:0_2px_28px_rgba(45,35,10,0.65)]">
                        <MaskedLine delay={0.15}>Unforgettable</MaskedLine>
                        <MaskedLine delay={0.3}>outdoor moments,</MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="italic text-[#FFD2CE]">styled in McKinney</span>
                        </MaskedLine>
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.1 }}
                        className="mt-7 max-w-xl text-[#FAF7F2]/95 leading-relaxed text-base sm:text-lg [text-shadow:0_1px_16px_rgba(45,35,10,0.6)]"
                    >
                        Proposals, date nights, bridal showers, birthdays and anniversaries —
                        candlelit luxury escapes with floor cushion seating, low handcrafted
                        tables and refined florals, anywhere in the DFW area.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 1.3 }}
                        className="mt-10 flex flex-wrap gap-4"
                    >
                        <a
                            href={HONEYBOOK_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-honeybook-cta"
                            className="group inline-flex items-center gap-2 bg-[#FFD2CE] hover:bg-[#FAF7F2] text-[#3D2E0A] px-8 py-4 rounded-full font-accent text-xs uppercase tracking-[0.18em] font-semibold transition-all duration-300 shadow-lg hover:scale-[1.04]"
                        >
                            Inquire Through HoneyBook
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                        <Link
                            to="/packages"
                            data-testid="hero-packages-link"
                            className="inline-flex items-center gap-2 border border-[#FAF7F2]/50 text-[#FAF7F2] hover:bg-[#FAF7F2]/10 hover:border-[#FAF7F2] px-8 py-4 rounded-full font-accent text-xs uppercase tracking-[0.18em] transition-all duration-300"
                        >
                            View Packages
                        </Link>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 1 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-3"
                    aria-hidden="true"
                >
                    <span className="font-accent text-[10px] uppercase tracking-[0.3em] text-[#FAF7F2]/60">Scroll</span>
                    <div className="w-px h-12 bg-[#FAF7F2]/25 overflow-hidden">
                        <div className="scroll-hint-line w-full h-full bg-[#FFD2CE]" />
                    </div>
                </motion.div>
            </section>

            {/* Editorial Marquee */}
            <section className="bg-[#2D230A] py-7 sm:py-9 border-y border-[#D4A359]/25">
                <Marquee items={MARQUEE_ITEMS} duration={50} />
            </section>

            {/* Numbered Manifesto */}
            <section className="py-24 sm:py-36" data-testid="home-manifesto">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-16 sm:mb-20">
                            The Enchanting Escapes Manifesto
                        </p>
                    </Reveal>
                    <div className="space-y-20 sm:space-y-28">
                        {CHAPTERS.map((c, i) => (
                            <div
                                key={c.n}
                                data-testid={`manifesto-chapter-${c.n}`}
                                className={`grid md:grid-cols-12 gap-6 md:gap-10 items-start ${i % 2 === 1 ? "md:text-right" : ""}`}
                            >
                                <Reveal className={`md:col-span-3 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                                    <span className="chapter-number">{c.n}</span>
                                </Reveal>
                                <Reveal delay={0.15} className={`md:col-span-9 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D230A] leading-tight">
                                        {c.title}
                                    </h2>
                                    <p className={`mt-5 text-[#6E5B3D] leading-relaxed text-base sm:text-lg max-w-xl ${i % 2 === 1 ? "md:ml-auto" : ""}`}>
                                        {c.text}
                                    </p>
                                </Reveal>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Cinematic Reel — real work */}
            <section ref={reelRef} className="pb-24 sm:pb-36" data-testid="home-reel">
                <div className="max-w-5xl mx-auto px-5 sm:px-8">
                    <Reveal className="text-center mb-10">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            The Film
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D230A] leading-tight">
                            Real escapes, in motion
                        </h2>
                    </Reveal>
                    <motion.div
                        style={{ scale: reelScale }}
                        className="rounded-[2rem] overflow-hidden shadow-2xl will-change-transform"
                    >
                        <video
                            data-testid="reel-video"
                            className="w-full aspect-video object-cover"
                            src={MEDIA.reel}
                            poster="/media/reel-poster.jpg"
                            autoPlay
                            muted
                            loop
                            playsInline
                            controls
                        />
                    </motion.div>
                </div>
            </section>

            {/* Client Stories */}
            <section className="pb-24 sm:pb-36" data-testid="home-stories">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal className="max-w-2xl mb-14 sm:mb-20">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                            Stories From Our Escapes
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D230A] leading-tight">
                            Moments we'll never forget — and neither will they
                        </h2>
                    </Reveal>
                    <div className="space-y-16 sm:space-y-20">
                        {[
                            {
                                quote: "Ashley worked out every detail with me to set this up for my wife to celebrate our wedding anniversary. She even went to our hotel and set up the room. Much love Ashley, you are amazing.",
                                name: "John Wayne",
                                detail: "Anniversary · 5-Star Google Review",
                                testid: "story-anniversary",
                            },
                            {
                                quote: "When we arrived, everything was already set up and looked picture-perfect — it honestly felt like stepping into a fairytale! She made our day stress free, stunning, and so special.",
                                name: "Husna Hamidee",
                                detail: "1st Birthday Picnic · 5-Star Google Review",
                                testid: "story-birthday",
                            },
                            {
                                quote: "I reached out with less than a week's notice for my sister's bridal shower picnic, and she made everything come together so seamlessly. The setup was even better than I imagined — I would recommend her over and over again.",
                                name: "Verified Google Review",
                                detail: "Bridal Shower · 5-Star Google Review",
                                testid: "story-shower",
                            },
                        ].map((s, i) => (
                            <Reveal key={s.testid} delay={0.05}>
                                <figure
                                    data-testid={s.testid}
                                    className={`grid md:grid-cols-12 gap-6 items-start ${i % 2 === 1 ? "md:text-right" : ""}`}
                                >
                                    <div className={`md:col-span-2 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                                        <span className="font-display text-7xl sm:text-8xl leading-none text-[#E8C4C1] select-none">"</span>
                                    </div>
                                    <div className={`md:col-span-10 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                                        <div className={`flex gap-0.5 mb-4 ${i % 2 === 1 ? "md:justify-end" : ""}`}>
                                            {[...Array(5)].map((_, st) => (
                                                <Star key={st} className="w-4 h-4 fill-[#D4A359] text-[#D4A359]" />
                                            ))}
                                        </div>
                                        <blockquote className={`font-display italic text-2xl sm:text-3xl text-[#2D230A] leading-snug max-w-2xl ${i % 2 === 1 ? "md:ml-auto" : ""}`}>
                                            {s.quote}
                                        </blockquote>
                                        <figcaption className="mt-4">
                                            <span className="font-accent text-xs uppercase tracking-[0.2em] text-[#554011] font-semibold">
                                                {s.name}
                                            </span>
                                            <span className="font-accent text-xs uppercase tracking-[0.2em] text-[#C18E8B] ml-3">
                                                {s.detail}
                                            </span>
                                        </figcaption>
                                    </div>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-14 text-center">
                        <a
                            href={GOOGLE_BUSINESS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="stories-google-link"
                            className="inline-flex items-center gap-2 font-accent text-xs uppercase tracking-[0.18em] text-[#554011] hover:text-[#C18E8B] transition-colors"
                        >
                            <span className="flex gap-0.5">
                                {[...Array(5)].map((_, s) => (
                                    <Star key={s} className="w-4 h-4 fill-[#D4A359] text-[#D4A359]" />
                                ))}
                            </span>
                            Read more 5-star stories on Google
                        </a>
                    </Reveal>
                </div>
            </section>

            {/* Occasions — editorial arch frames */}
            <section className="pb-24 sm:pb-36" data-testid="home-occasions">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal className="flex items-end justify-between mb-14 sm:mb-20 flex-wrap gap-4">
                        <div>
                            <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4">
                                Occasions We Style
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D230A] leading-tight">
                                Every celebration<br />deserves an escape
                            </h2>
                        </div>
                        <Link
                            to="/gallery"
                            data-testid="home-gallery-link"
                            className="group font-accent text-xs uppercase tracking-[0.18em] text-[#554011] hover:text-[#C18E8B] transition-colors inline-flex items-center gap-2"
                        >
                            View Full Gallery <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 sm:gap-5">
                        {OCCASIONS.map((o, i) => (
                            <Reveal key={o.key} delay={i * 0.12}>
                                <Link
                                    to={`/gallery#${o.key}`}
                                    data-testid={`occasion-card-${o.key}`}
                                    className="group block"
                                >
                                    <div className="arch-frame overflow-hidden aspect-[3/4] shadow-sm group-hover:shadow-2xl transition-shadow duration-500">
                                        <img
                                            src={o.image}
                                            alt={o.alt}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-[1.07] transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                                        />
                                    </div>
                                    <h3 className="font-display text-2xl text-[#2D230A] mt-5 group-hover:text-[#C18E8B] transition-colors">
                                        {o.label}
                                    </h3>
                                    <p className="mt-1 text-[#6E5B3D] text-sm leading-snug">{o.title}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Spotlight detail strip */}
            <section className="pb-24 sm:pb-36">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6">
                    <Reveal>
                        <ParallaxImage
                            src={MEDIA.fountain}
                            alt="Luxury picnic by Enchanting Escapes styled beside a fountain with candlelight in McKinney"
                            className="rounded-[2rem] aspect-[4/5]"
                        />
                    </Reveal>
                    <Reveal delay={0.15} className="md:mt-24">
                        <ParallaxImage
                            src="/media/sunset-table.jpg"
                            alt="Real Enchanting Escapes lakeside tablescape at golden hour"
                            className="rounded-[2rem] aspect-[4/5]"
                        />
                    </Reveal>
                </div>
            </section>

            {/* Service area */}
            <section className="bg-[#FFD2CE]/40 py-20 sm:py-28" data-testid="home-service-area">
                <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
                    <Reveal>
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4 flex items-center justify-center gap-2">
                            <MapPin className="w-4 h-4" /> Service Area
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D230A] leading-tight">
                            Proudly based in McKinney,<br />serving Dallas-Fort Worth
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-5 text-[#6E5B3D] max-w-2xl mx-auto leading-relaxed">
                            From lakeside proposals in McKinney to rooftop date nights in
                            Dallas, we bring the full styled experience to you — setup,
                            styling and complete breakdown included.
                        </p>
                    </Reveal>
                    <div className="mt-10 flex flex-wrap justify-center gap-3">
                        {SERVICE_AREA.map((city, i) => (
                            <Reveal key={city} delay={0.05 * i} y={16}>
                                <span
                                    data-testid={`service-city-${city.toLowerCase().replace(/\s/g, "-")}`}
                                    className="inline-block bg-white border border-[#E8C4C1] text-[#554011] px-5 py-2 rounded-full font-accent text-xs uppercase tracking-widest hover:border-[#C18E8B] hover:-translate-y-0.5 transition-all duration-300 cursor-default"
                                >
                                    {city}
                                </span>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Instagram */}
            <section className="py-20 sm:py-28" data-testid="home-instagram-section">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal className="text-center mb-12">
                        <p className="font-accent text-xs uppercase tracking-[0.25em] text-[#C18E8B] mb-4 flex items-center justify-center gap-2">
                            <Instagram className="w-4 h-4" /> Follow Along
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl tracking-tight text-[#2D230A]">
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="instagram-handle-link"
                                className="hover:text-[#C18E8B] transition-colors"
                            >
                                {INSTAGRAM_HANDLE}
                            </a>
                        </h2>
                        <p className="mt-3 text-[#6E5B3D]">
                            Recent escapes from McKinney, Dallas and across DFW.
                        </p>
                        <a
                            href={TIKTOK_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="tiktok-handle-link"
                            className="mt-4 inline-flex items-center gap-2 font-accent text-xs uppercase tracking-[0.18em] text-[#554011] hover:text-[#C18E8B] transition-colors"
                        >
                            <TikTokIcon className="w-4 h-4" /> Follow us on TikTok {TIKTOK_HANDLE}
                        </a>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                        {["/media/anniversary.jpg", "/media/adriatica.jpg", "/media/blush-tent.jpg", "/media/movie-night.jpg"].map((src, i) => (
                            <Reveal key={i} delay={i * 0.1}>
                                <a
                                    href={INSTAGRAM_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`instagram-tile-${i}`}
                                    className="group relative block rounded-2xl overflow-hidden aspect-square"
                                >
                                    <img
                                        src={src}
                                        alt={`Enchanting Escapes styled picnic moment ${i + 1}`}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-[#554011]/0 group-hover:bg-[#554011]/30 transition-colors duration-500 flex items-center justify-center">
                                        <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                    </div>
                                </a>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="text-center mt-10">
                        <a
                            href={GOOGLE_BUSINESS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="home-google-business-link"
                            className="inline-flex items-center gap-2 font-accent text-xs uppercase tracking-[0.18em] text-[#554011] hover:text-[#C18E8B] transition-colors"
                        >
                            <span className="flex gap-0.5">
                                {[...Array(5)].map((_, s) => (
                                    <Star key={s} className="w-4 h-4 fill-[#D4A359] text-[#D4A359]" />
                                ))}
                            </span>
                            See our 5-star reviews on Google
                        </a>
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </main>
    );
}
